import { z } from 'zod';
import {
  backorderReportSchema,
  orgUnitReportSchema,
  pipelineReportSchema,
  receiptTypeReportSchema,
  requisitionReportSchema,
  stockFlowReportSchema,
  subdivisionReportRowSchema,
  topItemsReportSchema,
  warehouseReportSchema,
  type BackorderReport,
  type OrgUnitReport,
  type PipelineReport,
  type ReceiptTypeReport,
  type ReportRange,
  type RequisitionReport,
  type StockFlowReport,
  type TopItemsReport,
  type WarehouseReport,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Hisobot endpointlari. Manba:
 *   `stock/infrastructure/http/report.controller.ts`
 *   `requisition/infrastructure/http/report.controller.ts`
 *   `subdivision/infrastructure/http/report.controller.ts`
 *
 * To'rtta ALOHIDA so'rov, bitta "dashboard" endpointi emas: har blok o'z
 * skeletoni bilan yuklanadi va bittasining xatosi qolganini o'chirmaydi.
 */
export const subdivisionReportPageSchema = z.object({
  items: z.array(subdivisionReportRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export type SubdivisionReportPage = z.infer<typeof subdivisionReportPageSchema>;

export interface SubdivisionReportQuery {
  q?: string;
  page?: number;
  limit?: number;
}

export interface ReportApi {
  stockFlow: (range: ReportRange) => Promise<StockFlowReport>;
  topItems: (range: ReportRange) => Promise<TopItemsReport>;
  requisitions: (range: ReportRange) => Promise<RequisitionReport>;
  subdivisions: (query?: SubdivisionReportQuery) => Promise<SubdivisionReportPage>;
  /** Omborlar kesimi va voronkalar DAVRSIZ — ular joriy holatni beradi. */
  warehouses: () => Promise<WarehouseReport>;
  receipts: (range: ReportRange) => Promise<ReceiptTypeReport>;
  pipeline: () => Promise<PipelineReport>;
  orgUnits: (range: ReportRange) => Promise<OrgUnitReport>;
  backorders: () => Promise<BackorderReport>;
}

const rangeString = (range: ReportRange): string =>
  new URLSearchParams({ from: range.from, to: range.to }).toString();

export const createReportApi = (client: ApiClient): ReportApi => ({
  stockFlow: (range) =>
    client.apiFetch(`/reports/stock-flow?${rangeString(range)}`, { schema: stockFlowReportSchema }),

  topItems: (range) =>
    client.apiFetch(`/reports/top-items?${rangeString(range)}`, { schema: topItemsReportSchema }),

  requisitions: (range) =>
    client.apiFetch(`/reports/requisitions?${rangeString(range)}`, {
      schema: requisitionReportSchema,
    }),

  warehouses: () =>
    client.apiFetch('/reports/warehouses', { schema: warehouseReportSchema }),

  receipts: (range) =>
    client.apiFetch(`/reports/receipts?${rangeString(range)}`, { schema: receiptTypeReportSchema }),

  pipeline: () => client.apiFetch('/reports/pipeline', { schema: pipelineReportSchema }),

  orgUnits: (range) =>
    client.apiFetch(`/reports/org-units?${rangeString(range)}`, { schema: orgUnitReportSchema }),

  backorders: () => client.apiFetch('/reports/backorders', { schema: backorderReportSchema }),

  subdivisions: (query = {}) => {
    const params = new URLSearchParams({
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 10),
    });
    if (query.q !== undefined && query.q !== '') params.set('q', query.q);
    return client.apiFetch(`/reports/subdivisions?${params.toString()}`, {
      schema: subdivisionReportPageSchema,
    });
  },
});

/** Ilovaning yagona klienti ustidagi tayyor ko'rinish. */
export const reportApi = createReportApi(apiClient);
